/**
 * An ISO-8601 date, optionally with a time, fractional seconds, an offset (`Z`,
 * `±HH`, `±HHMM`, `±HH:MM`, with or without a space before it) and a zone name
 * after it. Go's `time.Time` rendering — `2026-10-20 15:10:10.569 +0000 UTC` —
 * is the shape that started this.
 *
 * Matched and validated by hand rather than handed to `Date.parse`, whose
 * handling of anything outside strict ISO is implementation-defined (the
 * replay runs in whichever browser opened the grid, not just in V8), and which
 * rolls an impossible date like `2026-02-30` over to March 2 rather than
 * refusing it the way DuckDB does.
 */
const ISO_LIKE =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?)?\s*(?:(Z)|([+-])(\d{2})(?::?(\d{2}))?)?(?:\s+([A-Za-z][\w/+-]*))?$/i;

/** Zone names that mean UTC — the only ones read here rather than by V8. */
const UTC_ZONE_NAME = /^(?:UTC?|GMT|Z)$/i;

/**
 * A bare `YYYY` or `YYYY-MM`. ISO, so `Date.parse` reads it as UTC by spec,
 * unlike every other shape that reaches the fallback.
 */
const ISO_YEAR_OR_MONTH = /^\d{4}(?:-\d{2})?$/;

/**
 * Zone markers V8's fallback parser recognises: `Z`, `UT`/`UTC`/`GMT`, the US
 * abbreviations, and numeric offsets. A value with none is read in the local
 * zone of whatever process parses it.
 */
const ZONE_MARKER = /\b(?:Z|UTC?|GMT|[ECMP][SD]T)\b|[+-]\d{1,2}:?\d{2}\b/gi;

type IsoParts = {
  readonly date: string;
  readonly year: number;
  readonly month: number;
  readonly day: number;
  readonly hour: number;
  readonly minute: number;
  readonly second: number;
  readonly millisecond: number;
  readonly hasTime: boolean;
  readonly hasSeconds: boolean;
  /** Minutes east of UTC, or null when the value names no zone. */
  readonly offsetMinutes: number | null;
  /** The value's offset is a literal `Z` and nothing else. */
  readonly isZulu: boolean;
};

/**
 * The parts of an ISO-like value, `'invalid'` when it is ISO-shaped but names a
 * date or time that does not exist, or null when it is not ISO-shaped at all
 * (or names a zone only V8 can resolve, like `EST`).
 */
const parseIsoLike = (value: string): IsoParts | 'invalid' | null => {
  const match = ISO_LIKE.exec(value);
  if (match === null) {
    return null;
  }
  const [, y, mo, d, h, mi, s, fraction, zulu, sign, oh, om, zoneName] = match;
  const year = Number(y);
  const month = Number(mo);
  const day = Number(d);
  const hour = Number(h ?? '0');
  const minute = Number(mi ?? '0');
  const second = Number(s ?? '0');
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (
    month < 1 ||
    month > 12 ||
    day < 1 ||
    day > daysInMonth ||
    hour > 23 ||
    minute > 59 ||
    second > 59 ||
    (sign !== undefined && (Number(oh) > 23 || Number(om ?? '0') > 59))
  ) {
    return 'invalid';
  }
  let offsetMinutes: number | null = null;
  if (zulu !== undefined) {
    offsetMinutes = 0;
  } else if (sign !== undefined) {
    offsetMinutes =
      (sign === '-' ? -1 : 1) * (Number(oh) * 60 + Number(om ?? '0'));
  } else if (zoneName !== undefined) {
    if (!UTC_ZONE_NAME.test(zoneName)) {
      return null;
    }
    offsetMinutes = 0;
  }
  return {
    date: `${y}-${mo}-${d}`,
    year,
    month,
    day,
    hour,
    minute,
    second,
    millisecond: Number((fraction ?? '0').slice(0, 3).padEnd(3, '0')),
    hasTime: h !== undefined,
    hasSeconds: s !== undefined,
    offsetMinutes,
    isZulu: zulu !== undefined && zoneName === undefined,
  };
};

/**
 * Whether both DuckDBs that replay the log — the server's and the browser's
 * WASM build — cast this value the same way, as written.
 *
 * A zone-less ISO date or date-time qualifies, and so does one ending in a bare
 * `Z` once it has seconds (both refuse `15:10Z`). A numeric offset never does,
 * even a valid one: the server's build drops it (`23:10-07:00` → 23:10) while
 * the browser's applies it (→ 06:10 the next day), so the grid and the
 * compacted artifact would disagree on the same value.
 */
const isPortable = (parts: IsoParts): boolean =>
  parts.offsetMinutes === null || (parts.isZulu && parts.hasSeconds);

const isoInstant = (parts: IsoParts): string =>
  new Date(
    Date.UTC(
      parts.year,
      parts.month - 1,
      parts.day,
      parts.hour,
      parts.minute,
      parts.second,
      parts.millisecond
    ) -
      (parts.offsetMinutes ?? 0) * 60_000
  ).toISOString();

/** The wall time a local-zone `Date` holds, as the same wall time in UTC. */
const localWallTimeAsUtc = (local: Date): Date =>
  new Date(
    Date.UTC(
      local.getFullYear(),
      local.getMonth(),
      local.getDate(),
      local.getHours(),
      local.getMinutes(),
      local.getSeconds(),
      local.getMilliseconds()
    )
  );

/** Whether a DuckDB column type is one {@link normalizeDuckDbTimestamp} is for. */
export const isDuckDbTimestampColumnType = (type: string | null): boolean => {
  if (type === null) {
    return false;
  }
  const upper = type.trim().toUpperCase();
  return upper.startsWith('TIMESTAMP') || upper === 'DATE';
};

/**
 * Whether a value can go into a `TIMESTAMP` / `DATE` column as written, and be
 * read the same way by every DuckDB that replays it. What
 * {@link normalizeDuckDbTimestamp} produces for any value it can read, so a
 * write guard can refuse whatever it could not.
 */
export const isPortableDuckDbTimestamp = (value: string): boolean => {
  const parts = parseIsoLike(value.trim());
  return parts !== null && parts !== 'invalid' && isPortable(parts);
};

/**
 * A timestamp string every DuckDB that replays the log will cast the same way,
 * for one JavaScript can read.
 *
 * The write guard used to admit a temporal value whenever `Date.parse` accepted
 * it, and V8's parser is far more lenient than DuckDB's: Go's `time.Time`
 * rendering (`2026-10-20 15:10:10.569 +0000 UTC`), a bare year, `10/20/2026`
 * all parse in JavaScript and all raise `Conversion Error` in DuckDB. Once one
 * of those is in the operation log, every replay of it — the grid's in the
 * browser, and compaction's on the server — fails, and the dataset will not
 * open.
 *
 * - A portable value (see {@link isPortableDuckDbTimestamp}) is kept as is.
 * - Anything else that names an instant is rewritten to it, as ISO with `Z`.
 * - A value that names no zone is read as UTC, never the local zone, so the
 *   server and every browser land on the same instant.
 * - For a `DATE` column it becomes the calendar date as written —
 *   `2026-10-20 23:10 -0700` is the 20th, not the UTC instant's 21st.
 * - A date that does not exist (`2026-02-30`), or a string nothing can read, is
 *   returned unchanged for the guard to refuse.
 *
 * Used on the way in, so new writes are stored in a shape both engines agree
 * on, and on replay, so a log written before that still opens.
 */
export const normalizeDuckDbTimestamp = (
  value: string,
  type: string
): string => {
  const trimmed = value.trim();
  const dateOnly = type.trim().toUpperCase() === 'DATE';
  const parts = parseIsoLike(trimmed);
  if (parts === 'invalid') {
    return value;
  }
  if (parts !== null) {
    if (isPortable(parts)) {
      return trimmed;
    }
    return dateOnly ? parts.date : isoInstant(parts);
  }

  const parsed = Date.parse(trimmed);
  if (Number.isNaN(parsed)) {
    return value;
  }
  let instant: Date;
  let wallDate: Date;
  if (ISO_YEAR_OR_MONTH.test(trimmed)) {
    instant = new Date(parsed);
    wallDate = instant;
  } else if (trimmed.match(ZONE_MARKER) !== null) {
    instant = new Date(parsed);
    // The date as written, in the zone the value names: read it again with
    // the zone stripped, then take that wall date.
    const zoneless = Date.parse(
      trimmed.replace(/\([^)]*\)/g, ' ').replace(ZONE_MARKER, ' ')
    );
    wallDate = Number.isNaN(zoneless)
      ? instant
      : localWallTimeAsUtc(new Date(zoneless));
  } else {
    instant = localWallTimeAsUtc(new Date(parsed));
    wallDate = instant;
  }
  return dateOnly ? wallDate.toISOString().slice(0, 10) : instant.toISOString();
};
